import { useMemo } from "react";

import { ROIBreakdownEnum, ViewToggle } from "@/shared/types";
import { ChannelData } from "@/shared/types";
import { calculateROI } from "@/shared/utils/calculateROI";
import { isInsufficientSampleSize } from "@/Utils/dataFilters";
import { Layer } from "@/Utils/hierarchy";

import { BreakdownDataProps } from "./chartUtils";

export const useChartData = (
  channelROI: Record<string, ChannelData>,
  insightROI: Record<string, ChannelData>,
  chartView: ViewToggle,
  chartLevel: ROIBreakdownEnum,
  isAscending: boolean,
  filterInsufficientData: boolean
) => {
  const mappedData = useMemo(() => {
    const data = chartView === ViewToggle.CHANNEL ? channelROI : insightROI;
    return mapAndSortBreakdownData(data, chartLevel, isAscending, filterInsufficientData);
  }, [channelROI, insightROI, chartView, chartLevel, isAscending, filterInsufficientData]);

  const channelTotals = useMemo(() => {
    const data = chartView === ViewToggle.CHANNEL ? channelROI : insightROI;
    return calculateTotals(data);
  }, [channelROI, insightROI, chartView]);

  return { mappedData, channelTotals };
};

const mapAndSortBreakdownData = (
  data: Record<string, ChannelData>,
  chartLevel: ROIBreakdownEnum,
  isAscending: boolean,
  filterInsufficientData: boolean
): BreakdownDataProps[] => {
  const mappedData = Object.values(data).reduce((acc, item) => {
    const totalSpend = Object.values(item.spend).reduce((acc, spend) => acc + (spend || 0), 0);
    const totalRevenue = Object.values(item.revenue).reduce(
      (acc, revenue) => acc + (revenue || 0),
      0
    );

    if (totalSpend === 0 && totalRevenue === 0) {
      return acc;
    }

    const totalROI = calculateROI(totalSpend, totalRevenue);
    acc.push({
      id: item.id,
      channel: item.channel,
      mappedName: item.mappedName,
      revenue: [
        item.revenue[Layer.Brand] || 0,
        item.revenue[Layer.Category] || 0,
        item.revenue[Layer.Product] || 0,
      ],
      spend: [
        item.spend[Layer.Brand] || 0,
        item.spend[Layer.Category] || 0,
        item.spend[Layer.Product] || 0,
      ],
      totalSpend,
      totalROI,
      sampleSize: item.sampleSize,
      unit: item.unit,
    });

    return acc;
  }, [] as BreakdownDataProps[]);

  return sortMappedData(mappedData, chartLevel, isAscending, filterInsufficientData);
};

const sortMappedData = (
  mappedData: BreakdownDataProps[],
  chartLevel: ROIBreakdownEnum,
  isAscending: boolean,
  filterInsufficientData: boolean
): BreakdownDataProps[] => {
  let sortedData = [...mappedData];

  if (chartLevel === ROIBreakdownEnum.ROI) {
    sortedData.sort((a, b) => {
      if (filterInsufficientData) {
        const sampleSizeComparison =
          Number(isInsufficientSampleSize(b)) - Number(isInsufficientSampleSize(a));
        if (sampleSizeComparison !== 0) return sampleSizeComparison;
      }

      return isAscending
        ? (a.totalROI || 0) - (b.totalROI || 0)
        : (b.totalROI || 0) - (a.totalROI || 0);
    });

    if (!filterInsufficientData) {
      sortedData = sortedData.filter((item) => !isInsufficientSampleSize(item));
    }
  } else {
    sortedData.sort((a, b) =>
      isAscending ? a.totalSpend - b.totalSpend : b.totalSpend - a.totalSpend
    );
  }

  return sortedData;
};

const calculateTotals = (ROI: Record<string, ChannelData>) => {
  const totals: { [key: string]: { totalRevenue: number; totalSpend: number } } = {};

  Object.entries(ROI).forEach(([channel, data]) => {
    const { spend, revenue } = data;

    const channelTotals = totals[channel] || { totalRevenue: 0, totalSpend: 0 };

    Object.values(spend).forEach((amount: number) => {
      channelTotals.totalSpend += amount;
    });

    Object.values(revenue).forEach((amount: number) => {
      channelTotals.totalRevenue += amount;
    });

    totals[channel] = channelTotals;
  });

  return totals;
};
